import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import SessionService from '../services/SessionService';
import { processAvatarUrl } from '../utils/avatarUtils';
import { formatPhoneNumber, isValidInternationalPhoneLength } from '../utils/formatters';
import { TimezoneSelect } from '../components/TimezoneSelect';
import {
  getSubscription,
  getPlans,
  createCheckout,
  createPortalSession,
  getInvoices,
  type SubscriptionData,
  type PlanData,
  type PaymentData
} from '../services/SubscriptionService';
import './ProfilePage.css';
import { LoadingSpinner } from '../components/LoadingSpinner';

const PROFILE_UPDATED_EVENT = 'profileUpdated';
const API_URL = process.env.REACT_APP_API_URL;

const ProfilePage: React.FC = () => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [avatarBase64, setAvatarBase64] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: 'px',
    width: 200,
    height: 200,
    x: 0,
    y: 0
  });
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);

  // Configurações (timezone padrão para agendamento/broadcast)
  const [defaultTimezone, setDefaultTimezone] = useState<string>('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [profileSubTab, setProfileSubTab] = useState<'profile' | 'settings' | 'subscription'>('profile');
  const [searchParams, setSearchParams] = useSearchParams();

  // Subscription tab state
  const [subscription, setSubscription] = useState<SubscriptionData | null | undefined>(undefined);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [invoices, setInvoices] = useState<PaymentData[]>([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  useEffect(() => {
    console.log('useEffect running - Inicializando carregamento de dados do perfil');
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const token = SessionService.getToken();
        if (!token) {
          console.error('Token não encontrado');
          setIsLoading(false);
          return;
        }

        console.log('Realizando requisição GET para obter dados do perfil');
        const response = await fetch(`${API_URL}/users/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Erro na resposta:', response.status, errorText);
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        // Formato da resposta:
        // {
        //   "message": "Success",
        //   "code": "200",
        //   "data": {
        //     "id": 4,
        //     "name": "Gustavo Ribeiro",
        //     "email": "gustavo.ribeiro01001@gmail.com",
        //     "avatarUrl": null,
        //     "phoneWhatsapp": "(15) 99184-4611"
        //   }
        // }

        const responseData = await response.json();
        console.log('Resposta completa da API:', responseData);

        if (responseData && responseData.data) {
          const userData = responseData.data;
          console.log('Dados do usuário recebidos:', userData);

          // Atualiza os campos com os dados recebidos
          if (userData.name) {
            console.log('Definindo nome:', userData.name);
            setName(userData.name);
          }

          if (userData.email) {
            console.log('Definindo email:', userData.email);
            setEmail(userData.email);
          }

          if (userData.phoneWhatsapp) {
            console.log('Definindo telefone:', userData.phoneWhatsapp);
            const normalized = formatPhoneNumber(userData.phoneWhatsapp);
            setPhoneNumber(normalized);
            setIsPhoneValid(isValidInternationalPhoneLength(normalized));
          } else {
            setPhoneNumber('');
            setIsPhoneValid(false);
          }

          if (userData.avatarUrl) {
            const processedUrl = processAvatarUrl(userData.avatarUrl);
            setAvatar(processedUrl);
          } else {
            setAvatar(null);
          }

          if (userData.defaultTimezone != null && userData.defaultTimezone !== '') setDefaultTimezone(userData.defaultTimezone);
          else setDefaultTimezone('');

          console.log('Dados do usuário carregados com sucesso');
        } else {
          console.error('Estrutura de resposta inesperada:', responseData);
        }
      } catch (error) {
        console.error('Erro ao buscar dados do usuário', error);
      } finally {
        setIsLoading(false);
        console.log('Finalizado carregamento de dados do perfil');
      }
    };

    fetchUserData();
  }, []);

  // Read ?tab=subscription from URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'subscription') setProfileSubTab('subscription');
    if (tab === 'settings') setProfileSubTab('settings');
  }, [searchParams]);

  // Fetch subscription data when tab is subscription
  useEffect(() => {
    if (profileSubTab !== 'subscription') return;
    const fetchSubscriptionData = async () => {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      try {
        const [subRes, plansRes, invoicesRes] = await Promise.all([
          getSubscription(),
          getPlans(),
          getInvoices()
        ]);
        setSubscription(subRes.subscription ?? null);
        setPlans(plansRes.plans ?? []);
        setInvoices(invoicesRes.invoices ?? []);
      } catch (e) {
        setSubscriptionError(e instanceof Error ? e.message : 'Failed to load subscription');
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSubscriptionData();
  }, [profileSubTab]);

  function centerAspectCrop(mediaWidth: number, mediaHeight: number) {
    const cropSize = Math.min(mediaWidth, mediaHeight, 200);
    return centerCrop(
      makeAspectCrop(
        {
          unit: 'px',
          width: cropSize,
          height: cropSize
        },
        1,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImage(reader.result as string);
      const image = new Image();
      image.src = reader.result as string;
      image.onload = () => {
        const crop = centerAspectCrop(image.width, image.height);
        setCrop(crop);
        setImgRef(image);
      };
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const getCroppedImg = (image: string, crop: Crop): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = image;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Tamanho fixo para o avatar
        const size = 150;
        canvas.width = size;
        canvas.height = size;

        // Limpa o canvas e cria o círculo
        ctx.clearRect(0, 0, size, size);
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();

        // Calcula a escala correta
        const scaleX = img.naturalWidth / imgRef!.width;
        const scaleY = img.naturalHeight / imgRef!.height;

        // Calcula as dimensões do corte em pixels
        const pixelCrop = {
          x: crop.x * scaleX,
          y: crop.y * scaleY,
          width: crop.width * scaleX,
          height: crop.height * scaleY
        };

        // Aplica a suavização
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Desenha a imagem mantendo a proporção do corte
        ctx.drawImage(
          img,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          size,
          size
        );

        try {
          const base64Image = canvas.toDataURL('image/jpeg', 0.95);
          resolve(base64Image);
        } catch (err) {
          console.error('Error generating base64 image:', err);
          resolve('');
        }
      };

      img.onerror = () => {
        console.error('Error loading source image for cropping');
        resolve('');
      };
    });
  };

  const handleCropComplete = async () => {
    if (!crop.width || !crop.height) return;

    try {
      const croppedImageUrl = await getCroppedImg(cropImage, crop);
      setAvatarBase64(croppedImageUrl);
      setAvatar(croppedImageUrl);

      // Dispatch event with updated data and timestamp
      const event = new CustomEvent(PROFILE_UPDATED_EVENT, {
        detail: {
          avatarUrl: croppedImageUrl,
          name: name,
          timestamp: new Date().getTime()
        }
      });
      window.dispatchEvent(event);

      setShowCropModal(false);
    } catch (e) {
      console.error('Erro ao cortar imagem:', e);
    }
  };

  const handleSave = async () => {
    if (!isEmailValid || !isPhoneValid) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const token = SessionService.getToken();
      if (!token) {
        setErrorMessage('User not authenticated');
        setIsLoading(false);
        return;
      }

      // DTO seguro: só envia campos que existem; não envia avatarUrl vazio para não perder a foto
      const userData: Record<string, unknown> = {
        name,
        email,
        phoneWhatsapp: phoneNumber
      };
      if (avatarBase64) {
        userData.avatarUrl = avatarBase64;
      }

      console.log('Enviando dados (DTO seguro):', { ...userData, avatarUrl: userData.avatarUrl ? '[present]' : '[omitido]' });

      // Usando fetch API ao invés de axios
      const response = await fetch(`${API_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Erro na resposta:', response.status, errorData);
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('Resposta do servidor:', responseData);


      // Atualiza os dados do usuário com a resposta da API
      if (responseData && responseData.data) {
        const updatedUserData = responseData.data;

        // Atualiza os dados locais
        setName(updatedUserData.name || '');
        setEmail(updatedUserData.email || '');
        const savedPhone = updatedUserData.phoneWhatsapp
          ? formatPhoneNumber(String(updatedUserData.phoneWhatsapp))
          : '';
        setPhoneNumber(savedPhone);
        setIsPhoneValid(isValidInternationalPhoneLength(savedPhone));

        // Atualiza o avatar apenas se não estiver enviando um novo
        if (!avatarBase64 && updatedUserData.avatarUrl) {
          setAvatar(`${updatedUserData.avatarUrl}?t=${new Date().getTime()}`);
        }
        if (updatedUserData.defaultTimezone != null) setDefaultTimezone(updatedUserData.defaultTimezone);
      }

      // Exibe mensagem de sucesso
      setSuccessMessage(responseData.message || 'Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Dispatch event with updated data
      const event = new CustomEvent(PROFILE_UPDATED_EVENT, {
        detail: {
          avatarUrl: avatarBase64 || avatar,
          name: name,
          timestamp: new Date().getTime()
        }
      });
      window.dispatchEvent(event);
    } catch (error: any) {
      console.error('Erro ao atualizar o usuário:', error);
      setErrorMessage(
        error.message || 'Error updating profile. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(email));
  };

  /** Sem máscara: opcional + no início e até 15 dígitos (E.164). */
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.trim();
    let next = '';
    if (raw.startsWith('+')) {
      const rest = raw.slice(1).replace(/\D/g, '').slice(0, 15);
      next = rest.length > 0 ? `+${rest}` : '+';
    } else {
      next = raw.replace(/\D/g, '').slice(0, 15);
    }
    setPhoneNumber(next);
    setIsPhoneValid(isValidInternationalPhoneLength(next));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('handleNameChange value:', value);
    if (value.length <= 70) {
      setName(value);
    }
  };

  return (
    <div className="profile-page-wrapper">
      <div className="profile-screen">
        <header className="profile-header">
          <div className="profile-header-content">
            <h1 className="profile-title">Profile</h1>
            <p className="profile-subtitle">Manage your personal information and preferences</p>
          </div>
        </header>

        <nav className="profile-subnav" aria-label="Profile sections">
          <button
            type="button"
            className={`profile-subnav__link ${profileSubTab === 'profile' ? 'profile-subnav__link--active' : ''}`}
            onClick={() => setProfileSubTab('profile')}
          >
            My profile
          </button>
          <button
            type="button"
            className={`profile-subnav__link ${profileSubTab === 'settings' ? 'profile-subnav__link--active' : ''}`}
            onClick={() => setProfileSubTab('settings')}
          >
            Settings
          </button>
          <button
            type="button"
            className={`profile-subnav__link ${profileSubTab === 'subscription' ? 'profile-subnav__link--active' : ''}`}
            onClick={() => setProfileSubTab('subscription')}
          >
            Subscription
          </button>
        </nav>

        {isLoading && !name ? (
          <div className="profile-loading">
            <LoadingSpinner size="lg" color="primary" />
            <span>Loading profile...</span>
          </div>
        ) : (
          <main className="profile-main">
            {profileSubTab === 'profile' && (
              <section className="profile-card profile-card--personal">
              <h2 className="profile-card__title">Personal information</h2>
              <p className="profile-card__desc">Your name, email and phone. This info can be shown to contacts.</p>

              <div className="profile-card__avatar-wrap">
                <button
                  type="button"
                  className="profile-avatar"
                  onClick={() => document.getElementById('avatar-input')?.click()}
                  disabled={isLoading}
                  aria-label="Change profile photo"
                >
                  {avatar ? (
                    <img
                      src={avatar}
                      alt="Profile"
                      loading="eager"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        if (avatar.includes('supabase.co') && !avatar.includes('retry=1')) {
                          target.src = avatar.includes('?') ? `${avatar}&retry=1` : `${avatar}?retry=1`;
                        } else {
                          setAvatar(null);
                        }
                      }}
                    />
                  ) : (
                    <span className="profile-avatar__placeholder" aria-hidden>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2.5c-3.87 0-7 2.09-7 5v.5h14v-.5c0-2.91-3.13-5-7-5z" fill="currentColor" />
                      </svg>
                    </span>
                  )}
                </button>
                <label className="profile-avatar-edit" htmlFor="avatar-input">
                  Edit photo
                </label>
                <input
                  id="avatar-input"
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  className="profile-avatar-input"
                  disabled={isLoading}
                />
              </div>

              <div className="profile-fields">
                <div className="profile-field">
                  <label htmlFor="profile-name">Name</label>
                  <input
                    id="profile-name"
                    type="text"
                    className="profile-input"
                    value={name}
                    onChange={handleNameChange}
                    placeholder="Your name"
                    disabled={isLoading}
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-email">Email</label>
                  <input
                    id="profile-email"
                    type="email"
                    className={`profile-input ${!isEmailValid ? 'profile-input--error' : ''}`}
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="you@example.com"
                    disabled={isLoading}
                  />
                  {!isEmailValid && (
                    <span className="profile-field-error">Please enter a valid email address.</span>
                  )}
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-phone">Phone</label>
                  <input
                    id="profile-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    maxLength={16}
                    className={`profile-input ${!isPhoneValid ? 'profile-input--error' : ''}`}
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    placeholder="+1 5551234567 or 34600111222"
                    disabled={isLoading}
                  />
                  {!isPhoneValid && (
                    <span className="profile-field-error">
                      Enter 8–15 digits. Use + and country code when needed (E.164).
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                className="profile-btn profile-btn--primary"
                onClick={handleSave}
                disabled={isLoading || !isEmailValid || !isPhoneValid}
              >
                {isLoading ? <LoadingSpinner size="sm" color="white" /> : 'Save changes'}
              </button>

              {successMessage && <div className="profile-alert profile-alert--success">{successMessage}</div>}
              {errorMessage && <div className="profile-alert profile-alert--error">{errorMessage}</div>}
            </section>
            )}

            {profileSubTab === 'subscription' && (
            <section className="profile-card profile-card--subscription">
              <h2 className="profile-card__title">Subscription & Billing</h2>
              <p className="profile-card__desc">
                Manage your subscription, view payment history, and download invoices.
              </p>

              {subscriptionLoading ? (
                <div className="profile-loading">
                  <LoadingSpinner size="lg" color="primary" />
                  <span>Loading subscription...</span>
                </div>
              ) : subscriptionError ? (
                <div className="profile-alert profile-alert--error">{subscriptionError}</div>
              ) : (
                <>
                  {subscription ? (
                    <div className="profile-subscription-summary">
                      <div className="profile-subscription-row">
                        <span className="profile-subscription-label">Status</span>
                        <span className={`profile-subscription-value profile-subscription-status--${subscription.status}`}>
                          {subscription.status}
                        </span>
                      </div>
                      <div className="profile-subscription-row">
                        <span className="profile-subscription-label">Plan</span>
                        <span className="profile-subscription-value">{subscription.planName || '—'}</span>
                      </div>
                      <div className="profile-subscription-row">
                        <span className="profile-subscription-label">Expires</span>
                        <span className="profile-subscription-value">
                          {subscription.currentPeriodEnd
                            ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="profile-btn profile-btn--primary"
                        onClick={async () => {
                          try {
                            const { url } = await createPortalSession();
                            window.open(url, '_blank');
                          } catch (e) {
                            setSubscriptionError(e instanceof Error ? e.message : 'Failed to open portal');
                          }
                        }}
                      >
                        Manage subscription
                      </button>
                    </div>
                  ) : (
                    <div className="profile-subscription-summary">
                      <p className="profile-subscription-no-plan">No active subscription.</p>
                      {plans.length > 0 ? (
                        <div className="profile-subscription-plans">
                          <p className="profile-subscription-plans-title">Available plans:</p>
                          {plans.map((plan) => (
                            <div key={plan.id} className="profile-subscription-plan-item">
                              <span>{plan.name}</span>
                              <span>
                                ${(plan.amountCents / 100).toFixed(2)}/{plan.recurrence}
                              </span>
                              <button
                                type="button"
                                className="profile-btn profile-btn--primary profile-btn--sm"
                                onClick={async () => {
                                  try {
                                    const { url } = await createCheckout(plan.id);
                                    window.location.href = url;
                                  } catch (e) {
                                    setSubscriptionError(e instanceof Error ? e.message : 'Failed to start checkout');
                                  }
                                }}
                              >
                                Subscribe
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="profile-subscription-no-plans">No plans available. Please contact an administrator.</p>
                      )}
                    </div>
                  )}

                  <h3 className="profile-card__subtitle">Invoices</h3>
                  {invoices.length === 0 ? (
                    <p className="profile-subscription-empty">No invoices yet.</p>
                  ) : (
                    <table className="profile-subscription-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr key={inv.id}>
                            <td>{new Date(inv.createdAt).toLocaleDateString()}</td>
                            <td>${(inv.amountCents / 100).toFixed(2)}</td>
                            <td>{inv.status}</td>
                            <td>
                              {inv.invoicePdfUrl && (
                                <a href={inv.invoicePdfUrl} target="_blank" rel="noopener noreferrer">
                                  Download PDF
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </section>
            )}

            {profileSubTab === 'settings' && (
            <section className="profile-card profile-card--settings">
              <h2 className="profile-card__title">Schedule & broadcast defaults</h2>
              <p className="profile-card__desc">
                Default timezone used when you create a new schedule or broadcast. You can change it per item if needed.
              </p>

              <div className="profile-fields">
                <div className="profile-field">
                  <label htmlFor="profile-timezone">Timezone</label>
                  <TimezoneSelect
                    id="profile-timezone"
                    value={defaultTimezone}
                    onChange={setDefaultTimezone}
                    disabled={settingsSaving}
                    placeholder="Select timezone"
                  />
                </div>
              </div>

              <button
                type="button"
                className="profile-btn profile-btn--secondary"
                onClick={async () => {
                  setSettingsMessage(null);
                  setSettingsSaving(true);
                  try {
                    const token = SessionService.getToken();
                    if (!token) {
                      setSettingsMessage({ type: 'error', text: 'Not authenticated' });
                      return;
                    }
                    const res = await fetch(`${API_URL}/users/profile/settings`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                      },
                      body: JSON.stringify({
                        defaultTimezone: defaultTimezone || undefined,
                      }),
                    });
                    if (!res.ok) {
                      const err = await res.text();
                      throw new Error(err || 'Error saving settings');
                    }
                    setSettingsMessage({ type: 'success', text: 'Settings saved.' });
                    setTimeout(() => setSettingsMessage(null), 3000);
                  } catch (e: unknown) {
                    setSettingsMessage({
                      type: 'error',
                      text: e instanceof Error ? e.message : 'Error saving settings',
                    });
                  } finally {
                    setSettingsSaving(false);
                  }
                }}
                disabled={settingsSaving}
              >
                {settingsSaving ? <LoadingSpinner size="sm" color="white" /> : 'Save settings'}
              </button>

              {settingsMessage && (
                <div className={settingsMessage.type === 'success' ? 'profile-alert profile-alert--success' : 'profile-alert profile-alert--error'}>
                  {settingsMessage.text}
                </div>
              )}
            </section>
            )}
          </main>
        )}

        {showCropModal && (
          <div className="profile-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="crop-modal-title" onClick={() => setShowCropModal(false)}>
            <div className="profile-crop-modal" onClick={(e) => e.stopPropagation()}>
              <div className="profile-crop-modal__header">
                <h2 id="crop-modal-title">Adjust photo</h2>
                <button
                  type="button"
                  className="profile-crop-modal__close"
                  onClick={() => setShowCropModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="profile-crop-modal__body">
                {cropImage && (
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCrop(c)}
                    aspect={1}
                    circularCrop
                    minWidth={200}
                    minHeight={200}
                  >
                    <img
                      src={cropImage}
                      alt="Crop"
                      style={{ maxHeight: '70vh' }}
                      onLoad={(e) => {
                        const target = e.target as HTMLImageElement;
                        setImgRef(target);
                        setCrop(centerAspectCrop(target.width, target.height));
                      }}
                    />
                  </ReactCrop>
                )}
              </div>
              <div className="profile-crop-modal__footer">
                <button type="button" className="profile-btn profile-btn--ghost" onClick={() => setShowCropModal(false)}>
                  Cancel
                </button>
                <button type="button" className="profile-btn profile-btn--primary" onClick={handleCropComplete}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;


